import React, {useEffect, useRef} from 'react';
import {getStore} from 'src/v2/state/store';
import Loading from 'src/components/core/Loading';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {AnimatePresence, motion, tokens} from '@sail/motion';
import {useStateInitialize} from 'src/v2/state/useStateInitialize';
import {Header} from 'src/v2/components/screen/Header';
import {Layout} from 'src/v2/components/screen/Layout';
import {ScreenManager as RevampedScreenManager} from 'src/v2/components/screen/ScreenManager';
import {WalletConnectionClientProvider} from 'src/v2/state/walletConnection';
import {setupInContextMessageListener} from 'src/v2/util/inContextCompletion';
import {view} from '@sail/ui';
import {isEmbedded} from 'src/utils/utils';
import {useNotifyParentHeight} from 'src/v2/hooks/useNotifyParentHeight';

/**
 * V2Provider handles critical initialization for the v2 app.
 * This wrapper guarantees its children that:
 * - sessionModel is loaded and not null
 * - wallet connection iframe is ready and initialized
 */
const V2Provider = ({children}: {children: React.ReactNode}) => {
  const payinSessionContext = useSessionContext();
  const store = getStore();

  useStateInitialize(payinSessionContext);

  const isSessionLoaded = !!store.useSelector((state) => {
    return state.id.length > 0;
  });
  const isWalletConnectionReady = store.useSelector((state) => {
    return state.walletConnection.ready;
  });

  if (!isSessionLoaded) {
    return <Loading />;
  }

  return (
    <WalletConnectionClientProvider>
      {isWalletConnectionReady ? children : <Loading />}
    </WalletConnectionClientProvider>
  );
};

const App = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const embedded = isEmbedded();
  useNotifyParentHeight(embedded ? containerRef : null);

  useEffect(() => {
    return setupInContextMessageListener();
  }, []);

  return (
    <view.div ref={containerRef}>
      <V2Provider>
        <AnimatePresence>
          <view.div
            key="loaded"
            uses={[
              motion({
                animate: {opacity: 1},
                initial: {opacity: 0},
                transition: {ease: 'easeIn', duration: tokens.duration.default},
              }),
            ]}
          >
            <Layout header={<Header />}>
              <RevampedScreenManager />
            </Layout>
          </view.div>
        </AnimatePresence>
      </V2Provider>
    </view.div>
  );
};

export default App;
